import React from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import {
	PAYMENT_METHOD_EXAMPLES,
	POSTING_POLICY_EXAMPLES,
	STARTER_ACCOUNTS,
} from "../constants";

const OverviewTab = ({ status, isLoadingStatus, sessionOrganization, requiresOrganizationSelection }) => {
	if (isLoadingStatus) {
		return (
			<Box display="flex" justifyContent="center" py={4}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{!requiresOrganizationSelection && sessionOrganization && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Organisation: <strong>{sessionOrganization.name}</strong>
					{sessionOrganization.code ? ` (${sessionOrganization.code})` : ""}
				</Typography>
			)}

			<Alert severity={status?.accounting_enabled ? "success" : "warning"} sx={{ mb: 3 }}>
				{status?.message ||
					(status?.accounting_enabled
						? "Accounting auto-posting is active."
						: "Accounting auto-posting is disabled.")}
			</Alert>

			<Alert severity="info" sx={{ mb: 3 }}>
				Domain events (rent, deposits, payouts, expenses, broker commissions) are scheduled by the
				backend when those actions succeed. This screen only configures accounts, payment-method
				maps, posting policies, and reports — do not emit events from the UI for normal flows.
			</Alert>

			<Typography variant="h6" gutterBottom>
				Setup order
			</Typography>
			<Box component="ol" sx={{ pl: 3, mb: 3 }}>
				<li>
					Create chart of accounts (cash rails, landlord payable, security deposits, broker
					payable, income &amp; expense)
				</li>
				<li>Map payment methods → cash/wallet accounts</li>
				<li>
					Create a posting template (policy) per event type you use — mark the cash line with
					&quot;Use payment method&quot; where applicable
				</li>
				<li>New payments / payouts / expenses / commissions auto-post; use Event logs to debug</li>
				<li>Run Trial balance, General ledger, and Profit &amp; loss to verify books</li>
			</Box>

			<Typography variant="h6" gutterBottom>
				Suggested starter accounts (UGX)
			</Typography>
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				{STARTER_ACCOUNTS.map((row) => (
					<Typography key={row.code} variant="body2" sx={{ fontFamily: "monospace" }}>
						{row.code} — {row.name} ({row.account_type})
					</Typography>
				))}
			</Paper>

			<Typography variant="h6" gutterBottom>
				Payment method → account mapping
			</Typography>
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				{PAYMENT_METHOD_EXAMPLES.map((row) => (
					<Typography key={row.method} variant="body2">
						{row.method} → {row.account}
					</Typography>
				))}
			</Paper>

			<Typography variant="h6" gutterBottom>
				Posting policies (create one template per event)
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				Without a template for an event type, auto-posting logs as failed and does not block the
				domain action. Configure policies under Posting templates.
			</Typography>
			<Paper variant="outlined" sx={{ p: 2 }}>
				{POSTING_POLICY_EXAMPLES.map((policy) => (
					<Box key={policy.event_type} sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
						<Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
							{policy.event_type}
						</Typography>
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
							{policy.label} — {policy.when}
						</Typography>
						{policy.lines.map((line) => (
							<Typography
								key={`${policy.event_type}-${line.side}-${line.code}-${line.amount_key}`}
								variant="body2"
								sx={{ pl: 1 }}
							>
								{line.side} {line.code} — {line.amount_key}
								{line.uses_payment_method ? " (uses payment method)" : ""}
							</Typography>
						))}
					</Box>
				))}
			</Paper>
		</Box>
	);
};

export default OverviewTab;
